import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  RotateCcw,
  Users,
  Search,
  AlertTriangle,
  Loader2,
  ArrowRight,
  Eye,
  Link2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Smartphone,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Label } from '../../../components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';

const ITEMS_PER_PAGE = 10;

const normalizeEmailDomain = (email) => {
  if (typeof email !== 'string') return 'N/A';

  const trimmedEmail = email.trim();
  if (!trimmedEmail) return 'N/A';

  const atIndex = trimmedEmail.lastIndexOf('@');
  if (atIndex === -1) return trimmedEmail;

  const localPart = trimmedEmail.slice(0, atIndex);
  const domainPart = trimmedEmail.slice(atIndex + 1).toLowerCase();
  return `${localPart}@${domainPart}`;
};

const normalizeAssignments = (rawAssignments = []) =>
  (rawAssignments || []).map((item) => {
    const device = item?.device || {};
    const admin = item?.admin || {};
    const account = item?.account || {};
    const deviceType = device?.device_type || {};
    const customers = account?.customers || [];
    const customer = Array.isArray(customers) && customers.length > 0 ? customers[0] : null;

    return {
      id:
        item?.id ||
        item?._id ||
        item?.customer_assignment_id ||
        item?.assignment_id ||
        null,
      deviceId:
        item?.device_id ||
        device?.device_id ||
        device?._id ||
        device?.id ||
        null,
      deviceName:
        deviceType?.name ||
        device?.device_name ||
        device?.name ||
        'N/A',
      imei:
        device?.imei_number ||
        device?.imeiNumber ||
        device?.imei ||
        device?.imeiNo ||
        '',
      deviceStatus: device?.device_status || device?.deviceStatus || '',
      assignedAt: item?.assigned_at || item?.created_at || item?.assignedAt,
      accountName: account?.name || 'N/A',
      accountId: account?.id || account?._id || item?.account_id || null,
      adminName: admin?.name || 'N/A',
      adminId: admin?.id || item?.admin_id || null,
      customerName: customer?.name || 'N/A',
      customerEmail: normalizeEmailDomain(customer?.email),
      customerId: customer?.id || null,
      notes: device?.notes || '',
    };
  });

const AllCustomerDevices = () => {
  // Responsive layout: mobile cards + desktop table (no viewMode toggle)
  const { get, patch } = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [assignments, setAssignments] = useState([]);
  const [meta, setMeta] = useState({
    currentPage: 1,
    perPage: ITEMS_PER_PAGE,
    total: 0,
    lastPage: 1,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [unassignDialog, setUnassignDialog] = useState({ isOpen: false, assignment: null });
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [moveDialog, setMoveDialog] = useState({ isOpen: false, assignment: null });
  const [isMoving, setIsMoving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [customerUsers, setCustomerUsers] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedCustomerUserId, setSelectedCustomerUserId] = useState('');
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);

  const normalizedRole = user?.role?.toLowerCase() || '';
  const isSuperAdmin = normalizedRole === 'superadmin' || normalizedRole === 'super_admin';
  const isCustomerUser = normalizedRole === 'customer' || normalizedRole === 'user';

  useEffect(() => {
    fetchAssignments(1);
  }, []);

  const getDetailsPath = (assignmentId) =>
    isCustomerUser
      ? `/customer/devices/${assignmentId}`
      : `/admin/customer-device-assignments/${assignmentId}`;

  const fetchAssignments = async (page = 1) => {
    const searchParams = new URLSearchParams({
      active: '1',
      per_page: ITEMS_PER_PAGE,
      page: page,
    });

    try {
      if (isLoading) {
        setAssignments([]);
      }

      const response = await get(`/customer-device-assignments?${searchParams.toString()}`);

      if (response?.success && Array.isArray(response?.data)) {
        setAssignments(normalizeAssignments(response.data));
        setMeta({
          currentPage: response?.meta?.currentPage || page,
          perPage: response?.meta?.perPage || ITEMS_PER_PAGE,
          total: response?.meta?.total || response?.data?.length || 0,
          lastPage: response?.meta?.lastPage || 1,
        });
      } else {
        setAssignments([]);
        setMeta({
          currentPage: page,
          perPage: ITEMS_PER_PAGE,
          total: 0,
          lastPage: 1,
        });
      }
    } catch (error) {
      toast.error(
        error?.message ||
          'Unable to load customer device assignments. Please try again.'
      );
      setAssignments([]);
      setMeta({
        currentPage: page,
        perPage: ITEMS_PER_PAGE,
        total: 0,
        lastPage: 1,
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchAssignments(meta.currentPage || 1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const openUnassignDialog = (assignment) => {
    setUnassignDialog({ isOpen: true, assignment });
  };

  const closeUnassignDialog = () => {
    setUnassignDialog({ isOpen: false, assignment: null });
  };

  const openMoveDialog = async (assignment) => {
    setMoveDialog({ isOpen: true, assignment });
    setSelectedAccountId('');
    setSelectedCustomerUserId('');
    await fetchAccounts();
  };

  const closeMoveDialog = () => {
    setMoveDialog({ isOpen: false, assignment: null });
    setSelectedAccountId('');
    setSelectedCustomerUserId('');
    setAccounts([]);
    setCustomerUsers([]);
  };

  const fetchAccounts = async () => {
    setIsLoadingAccounts(true);
    try {
      const response = await get('/accounts');
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setAccounts(data);
    } catch (error) {
      toast.error('Failed to load accounts');
      setAccounts([]);
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const fetchCustomerUsers = async (accountId) => {
    if (!accountId) {
      setCustomerUsers([]);
      return;
    }

    setIsLoadingCustomers(true);
    try {
      // Call API with account_id as query parameter
      // The API should filter customers by account_id on the server side
      const endpoint = `/customer-list?account_id=${accountId}`;
      const response = await get(endpoint);
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      
      // Use the data returned by the API (it should already be filtered by account_id)
      setCustomerUsers(data);
    } catch (error) {
      // If the query parameter approach fails, try without it and show all customers
      try {
        const response = await get('/customer-list');
        const data = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        // Show all customers as fallback
        setCustomerUsers(data);
      } catch (fallbackError) {
        toast.error('Failed to load customer users');
        setCustomerUsers([]);
      }
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  const handleAccountChange = (accountId) => {
    setSelectedAccountId(accountId);
    setSelectedCustomerUserId('');
    fetchCustomerUsers(accountId);
  };

  const handleMoveDevice = async () => {
    const assignment = moveDialog.assignment;
    if (!assignment || !selectedAccountId || !selectedCustomerUserId) {
      toast.error('Please select both account and customer user');
      return;
    }

    const assignmentId = assignment.id;
    if (!assignmentId) {
      toast.error('Assignment ID missing');
      return;
    }

    setIsMoving(true);
    try {
      const payload = {
        move_to_account_id: selectedAccountId,
        customer_user_id: selectedCustomerUserId,
      };

      const response = await patch(`/customer-device-assignments/${assignmentId}`, payload);

      toast.success(response?.message || 'Device moved successfully');
      closeMoveDialog();
      
      // Refresh the list after successful move
      await fetchAssignments(meta.currentPage || 1);
    } catch (error) {

      let errorMessage = 'An error occurred while moving the device. Please try again.';

      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsMoving(false);
    }
  };

  const handleUnassignDevice = async () => {
    const assignment = unassignDialog.assignment;
    if (!assignment) return;

    const assignmentId = assignment.id;
    if (!assignmentId) {
      toast.error('Assignment ID missing');
      return;
    }

    setIsUnassigning(true);
    try {

      const payload = { unassign_now: true };
      const response = await patch(`/customer-device-assignments/${assignmentId}`, payload);


      toast.success(response?.message || 'Device unassigned successfully');
      closeUnassignDialog();
      
      // Refresh the list after successful unassignment
      await fetchAssignments(meta.currentPage || 1);
    } catch (error) {

      let errorMessage = 'An error occurred while unassigning the device. Please try again.';

      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsUnassigning(false);
    }
  };

  const filteredAssignments = useMemo(() => {
    if (!searchTerm) return assignments;

    const term = searchTerm.toLowerCase();
    return assignments.filter((assignment) => {
      return (
        assignment.deviceId?.toLowerCase().includes(term) ||
        assignment.deviceName?.toLowerCase().includes(term) ||
        assignment.imei?.toLowerCase().includes(term) ||
        assignment.accountName?.toLowerCase().includes(term) ||
        assignment.adminName?.toLowerCase().includes(term) ||
        assignment.customerName?.toLowerCase().includes(term) ||
        assignment.customerEmail?.toLowerCase().includes(term)
      );
    });
  }, [assignments, searchTerm]);

  const totalAssignments = meta?.total || 0;
  const perPage = meta?.perPage || ITEMS_PER_PAGE;
  const rangeStart = totalAssignments === 0 ? 0 : (meta.currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(meta.currentPage * perPage, totalAssignments);
  const showEmptyState = !isLoading && filteredAssignments.length === 0;

  const panelClass =
    'overflow-hidden rounded-xl bg-card text-card-foreground shadow-sm dark:bg-[#18181b]';
  const mobileShellClass = 'block md:hidden space-y-2.5';
  const panelToolbarClass = 'flex items-center gap-2 px-0 py-2.5 sm:px-4';
  const panelFooterClass =
    'flex flex-col items-start justify-between gap-2 px-0 py-2.5 sm:flex-row sm:items-center sm:px-4';
  const searchWrapClass = 'relative min-w-0 flex-1';
  const searchInputClass =
    'h-10 w-full rounded-full border-0 bg-muted pl-10 pr-4 text-[13px] text-foreground shadow-none placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-[#27272a] dark:text-zinc-100 dark:placeholder:text-zinc-500';
  const toolbarButtonClass =
    'h-10 w-10 shrink-0 rounded-full border-0 bg-muted p-0 hover:bg-muted/80 dark:bg-[#27272a] dark:hover:bg-[#2f2f33]';
  const gridHeaderRowClass = 'border-0 hover:bg-transparent';
  const gridHeadClass =
    'h-9 px-3 py-0 text-[12px] font-medium text-muted-foreground select-none whitespace-nowrap';
  const gridCellClass =
    'px-3 py-1.5 text-[12px] leading-tight text-foreground align-middle whitespace-nowrap';
  const gridRowBaseClass =
    'h-9 border-0 transition-colors hover:bg-muted/50 dark:hover:bg-[#27272a]';
  const getGridRowClass = (index) =>
    cn(
      gridRowBaseClass,
      index % 2 === 1
        ? 'bg-muted/40 dark:bg-[#1f1f23]'
        : 'bg-background/50 dark:bg-[#18181b]'
    );
  const compactTableClass =
    'min-w-full text-xs [&_td]:p-0 [&_th]:p-0 [&_tr]:h-9 [&_tr]:border-0';
  const actionButtonClass =
    'h-7 w-7 shrink-0 rounded-md p-0 text-zinc-500 shadow-none hover:bg-zinc-100 hover:text-zinc-800 focus-visible:ring-0 focus-visible:ring-offset-0 dark:text-zinc-400 dark:hover:bg-[#27272a] dark:hover:text-zinc-200';
  const actionButtonDangerClass =
    'h-7 w-7 shrink-0 rounded-md p-0 text-red-500 shadow-none hover:bg-red-500/10 hover:text-red-600 focus-visible:ring-0 focus-visible:ring-offset-0 dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300';
  const mobileCardClass =
    'overflow-hidden rounded-xl border border-border/50 bg-card p-3.5 shadow-sm transition-transform active:scale-[0.99] dark:border-zinc-800/80 dark:bg-[#1f1f23]';
  const mobileCardInsetClass = 'rounded-lg bg-muted/70 px-2.5 py-2 dark:bg-[#27272a]';
  const mobileListClass = 'space-y-2.5';

  const formatAssignedDate = (value) =>
    value ? new Date(value).toLocaleDateString('en-GB') : 'N/A';

  const SkeletonRow = ({ index = 0 }) => (
    <TableRow className={getGridRowClass(index)}>
      <TableCell className={gridCellClass}>
        <Skeleton className="h-3.5 w-20" />
      </TableCell>
      <TableCell className={gridCellClass}>
        <Skeleton className="h-3.5 w-24" />
      </TableCell>
      <TableCell className={`${gridCellClass} hidden sm:table-cell`}>
        <Skeleton className="h-3.5 w-28" />
      </TableCell>
      <TableCell className={`${gridCellClass} hidden lg:table-cell`}>
        <Skeleton className="h-3.5 w-24" />
      </TableCell>
      <TableCell className={`${gridCellClass} hidden lg:table-cell`}>
        <Skeleton className="h-3.5 w-24" />
      </TableCell>
      <TableCell className={`${gridCellClass} hidden xl:table-cell`}>
        <Skeleton className="h-3.5 w-24" />
      </TableCell>
      <TableCell className={`${gridCellClass} hidden xl:table-cell`}>
        <Skeleton className="h-3.5 w-32" />
      </TableCell>
      <TableCell className={gridCellClass}>
        <Skeleton className="h-3.5 w-16" />
      </TableCell>
      <TableCell className={gridCellClass}>
        <div className="flex items-center gap-1">
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
        </div>
      </TableCell>
    </TableRow>
  );

  const renderStatusBadge = (status) => {
    if (!status) {
      return <span className="text-[12px] font-medium text-muted-foreground">Unknown</span>;
    }

    const normalized = status.toLowerCase();
    if (normalized === 'assigned') {
      return <span className="text-[12px] font-medium text-emerald-600 dark:text-emerald-400">Assigned</span>;
    }

    if (normalized === 'in_pool') {
      return <span className="text-[12px] font-medium text-blue-600 dark:text-blue-400">In Pool</span>;
    }

    return <span className="text-[12px] font-medium text-amber-600 dark:text-amber-400">{status}</span>;
  };

  const renderTruncatedCellText = (
    value,
    fallback = 'N/A',
    widthClass = 'max-w-[120px]'
  ) => {
    const normalizedValue =
      value === null || value === undefined || value === '' ? fallback : String(value);

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`block truncate ${widthClass} text-[12px]`}>
            {normalizedValue}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="bg-popover text-popover-foreground border border-border"
        >
          <p className="max-w-[320px] break-words">{normalizedValue}</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  const goToPage = (page) => {
    if (page < 1 || page > meta.lastPage || page === meta.currentPage) return;
    setIsLoading(true);
    fetchAssignments(page);
  };

  const renderSearchToolbar = () => (
    <div className={panelToolbarClass}>
      <div className={searchWrapClass}>
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" />
        <Input
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearchChange}
          className={searchInputClass}
        />
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleRefresh}
        className={toolbarButtonClass}
        disabled={isRefreshing}
        aria-label="Refresh assignments"
      >
        <RotateCcw className={cn('h-4 w-4 text-zinc-500 dark:text-zinc-400', isRefreshing && 'animate-spin')} />
      </Button>
    </div>
  );

  const renderPaginationFooter = () => (
    <div className={panelFooterClass}>
      {isLoading ? (
        <>
          <Skeleton className="h-4 w-40" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-20 rounded-lg" />
            <Skeleton className="h-8 w-24 rounded-lg" />
            <Skeleton className="h-8 w-20 rounded-lg" />
          </div>
        </>
      ) : (
        <>
          <p className="text-[12px] text-muted-foreground">
            {totalAssignments === 0
              ? 'Showing 0 assignments'
              : `Showing ${rangeStart}–${rangeEnd} of ${totalAssignments} assignments`}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goToPage(meta.currentPage - 1)}
              disabled={meta.currentPage <= 1}
              className="h-7 gap-1 rounded-md border-0 bg-muted/40 px-2.5 text-[12px] text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Prev
            </Button>
            <span className="inline-flex h-7 min-w-[80px] items-center justify-center rounded-md border-0 bg-muted/40 px-2.5 text-[12px] text-muted-foreground">
              Page {meta.currentPage} / {meta.lastPage}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goToPage(meta.currentPage + 1)}
              disabled={meta.currentPage >= meta.lastPage}
              className="h-7 gap-1 rounded-md border-0 bg-muted/40 px-2.5 text-[12px] text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const renderAssignmentActions = (assignment, buttonClass = actionButtonClass) => (
    <div className="flex items-center gap-0.5">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => navigate(getDetailsPath(assignment.id))}
        className={buttonClass}
        title="View details"
      >
        <Eye className="h-4 w-4" />
      </Button>
      {isSuperAdmin && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => openMoveDialog(assignment)}
          className={buttonClass}
          title="Move to another account"
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      )}
      {!isCustomerUser && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => openUnassignDialog(assignment)}
          className={actionButtonDangerClass}
          title="Unassign device"
        >
          <Link2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  const MobileAssignmentCardSkeleton = () => (
    <div className={mobileCardClass}>
      <div className="mb-3 flex items-center justify-between">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-4 w-16" />
      </div>
      <div className="mb-3 grid grid-cols-2 gap-2">
        <Skeleton className="h-14 rounded-lg" />
        <Skeleton className="h-14 rounded-lg" />
      </div>
      <Skeleton className="mb-3 h-12 rounded-lg" />
      <Skeleton className="mb-3 h-12 rounded-lg" />
      <Skeleton className="h-9 w-full rounded-lg" />
    </div>
  );

  const MobileAssignmentCard = ({ assignment }) => (
    <article className={mobileCardClass}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Device
          </p>
          <p className="truncate text-[13px] font-medium text-zinc-900 dark:text-zinc-100">
            {assignment.deviceName || 'N/A'}
          </p>
        </div>
        {renderStatusBadge(assignment.deviceStatus)}
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2">
        <div className={mobileCardInsetClass}>
          <div className="mb-1 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            <Clock className="h-3 w-3 shrink-0" />
            Assigned
          </div>
          <p className="text-[11px] font-medium leading-snug text-zinc-900 dark:text-zinc-100">
            {formatAssignedDate(assignment.assignedAt)}
          </p>
        </div>
        <div className={mobileCardInsetClass}>
          <div className="mb-1 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            <Smartphone className="h-3 w-3 shrink-0" />
            IMEI
          </div>
          <p className="break-all font-mono text-[10px] leading-snug text-zinc-700 dark:text-zinc-300">
            {assignment.imei || '—'}
          </p>
        </div>
      </div>

      <div className={`mb-3 space-y-1.5 ${mobileCardInsetClass}`}>
        <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
          Account
        </p>
        <p className="truncate text-[12px] font-medium text-zinc-900 dark:text-zinc-100">
          {assignment.accountName || 'N/A'}
        </p>
        <p className="truncate text-[11px] text-zinc-600 dark:text-zinc-400">
          Admin: {assignment.adminName || 'N/A'}
        </p>
      </div>

      <div className={`mb-3 space-y-1 ${mobileCardInsetClass}`}>
        <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
          Customer
        </p>
        <p className="truncate text-[12px] font-medium text-zinc-900 dark:text-zinc-100">
          {assignment.customerName || 'N/A'}
        </p>
        {assignment.customerEmail && assignment.customerEmail !== 'N/A' && (
          <p className="truncate text-[11px] text-zinc-600 dark:text-zinc-400">
            {assignment.customerEmail}
          </p>
        )}
      </div>

      <div className="flex items-center justify-end gap-1">
        {renderAssignmentActions(assignment)}
      </div>
    </article>
  );

  return (
    <>
      <div className="relative w-full max-w-full overflow-hidden">
        <div className="mb-3">
          <h1 className="text-lg font-bold text-zinc-900 sm:text-xl dark:text-zinc-100">
            Customer Device Assignments
          </h1>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            View all active device links between accounts and customer users.
          </p>
        </div>

        {/* Mobile cards */}
        <div className={mobileShellClass}>
          {renderSearchToolbar()}
          <div className={mobileListClass}>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, index) => (
                <MobileAssignmentCardSkeleton key={`skeleton-card-${index}`} />
              ))
            ) : showEmptyState ? (
              <div className={`${mobileCardClass} py-12 text-center`}>
                <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-zinc-400 dark:text-zinc-500" />
                <h3 className="mb-2 text-[15px] font-medium text-zinc-900 dark:text-zinc-100">
                  No assignments found
                </h3>
                <p className="text-[12px] text-zinc-500 dark:text-zinc-400">
                  {searchTerm
                    ? 'Try adjusting your search criteria.'
                    : 'Customer device assignments will appear here once created.'}
                </p>
              </div>
            ) : (
              filteredAssignments.map((assignment) => (
                <MobileAssignmentCard key={assignment.id || assignment.deviceId} assignment={assignment} />
              ))
            )}
          </div>
          {renderPaginationFooter()}
        </div>

        {/* Desktop table panel */}
        <div className={`hidden md:block ${panelClass}`}>
          <div className="px-3 py-2.5 sm:px-4">
            {renderSearchToolbar()}
          </div>
          <div className="overflow-x-auto">
            <Table className={compactTableClass}>
              <TableHeader className="[&_tr]:border-0">
                <TableRow className={gridHeaderRowClass}>
                  <TableHead className={`${gridHeadClass} min-w-[100px]`}>Assigned On</TableHead>
                  <TableHead className={`${gridHeadClass} min-w-[120px]`}>Device Name</TableHead>
                  <TableHead className={`${gridHeadClass} hidden min-w-[140px] sm:table-cell`}>IMEI</TableHead>
                  <TableHead className={`${gridHeadClass} hidden min-w-[120px] lg:table-cell`}>Account</TableHead>
                  <TableHead className={`${gridHeadClass} hidden min-w-[120px] lg:table-cell`}>Admin</TableHead>
                  <TableHead className={`${gridHeadClass} hidden min-w-[120px] xl:table-cell`}>Customer Name</TableHead>
                  <TableHead className={`${gridHeadClass} hidden min-w-[160px] xl:table-cell`}>Customer Email</TableHead>
                  <TableHead className={`${gridHeadClass} min-w-[90px]`}>Status</TableHead>
                  <TableHead className={`${gridHeadClass} min-w-[100px]`}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <SkeletonRow key={`skeleton-${index}`} index={index} />
                  ))
                ) : showEmptyState ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-12 text-center">
                      <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
                      <h3 className="mb-2 text-[15px] font-medium">No assignments found</h3>
                      <p className="text-[13px] text-muted-foreground">
                        {searchTerm
                          ? 'Try adjusting your search criteria.'
                          : 'Customer device assignments will appear here once created.'}
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAssignments.map((assignment, index) => (
                    <TableRow key={assignment.id || assignment.deviceId} className={getGridRowClass(index)}>
                      <TableCell className={gridCellClass}>
                        {renderTruncatedCellText(formatAssignedDate(assignment.assignedAt), 'N/A', 'max-w-[90px]')}
                      </TableCell>
                      <TableCell className={`${gridCellClass} font-medium`}>
                        {renderTruncatedCellText(assignment.deviceName, 'N/A', 'max-w-[110px]')}
                      </TableCell>
                      <TableCell className={`${gridCellClass} hidden font-mono text-muted-foreground sm:table-cell`}>
                        {renderTruncatedCellText(assignment.imei, 'N/A', 'max-w-[120px]')}
                      </TableCell>
                      <TableCell className={`${gridCellClass} hidden lg:table-cell`}>
                        {renderTruncatedCellText(assignment.accountName, 'N/A', 'max-w-[110px]')}
                      </TableCell>
                      <TableCell className={`${gridCellClass} hidden lg:table-cell`}>
                        {renderTruncatedCellText(assignment.adminName, 'N/A', 'max-w-[110px]')}
                      </TableCell>
                      <TableCell className={`${gridCellClass} hidden xl:table-cell`}>
                        {renderTruncatedCellText(assignment.customerName, 'N/A', 'max-w-[110px]')}
                      </TableCell>
                      <TableCell className={`${gridCellClass} hidden xl:table-cell`}>
                        {renderTruncatedCellText(assignment.customerEmail, 'N/A', 'max-w-[140px]')}
                      </TableCell>
                      <TableCell className={gridCellClass}>
                        {renderStatusBadge(assignment.deviceStatus)}
                      </TableCell>
                      <TableCell className={gridCellClass}>
                        {renderAssignmentActions(assignment)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="px-3 py-2.5 sm:px-4">
            {renderPaginationFooter()}
          </div>
        </div>
      </div>

      {/* Unassign Confirmation Dialog */}
      {unassignDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border-1 border-muted-foreground/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Unassign Device
                </h3>
                <p className="text-sm text-muted-foreground">
                  Confirm unassignment action
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-foreground">
                Are you sure you want to unassign the device from customer user?
              </p>
              {unassignDialog.assignment && (
                <div className="mt-3 p-3 bg-muted/50 rounded-md space-y-1">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">IMEI:</span> {unassignDialog.assignment.imei || 'N/A'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Account:</span> {unassignDialog.assignment.accountName || 'N/A'}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeUnassignDialog}
                className="px-4 py-2 border-0 bg-muted/50"
                disabled={isUnassigning}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUnassignDevice}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white"
                disabled={isUnassigning}
              >
                {isUnassigning ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Unassigning...
                  </>
                ) : (
                  'Unassign Device'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Move to Another Account Dialog */}
      {moveDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border-1 border-muted-foreground/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <ArrowRight className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Move to Another Account
                </h3>
                <p className="text-sm text-muted-foreground">
                  Transfer device to a different account and customer
                </p>
              </div>
            </div>
            
            <div className="mb-6 space-y-4">
              {moveDialog.assignment && (
                <div className="p-3 bg-muted/50 rounded-md space-y-1">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">IMEI:</span> {moveDialog.assignment.imei || 'N/A'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Current Account:</span> {moveDialog.assignment.accountName || 'N/A'}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">
                  New Account <span className="text-destructive">*</span>
                </Label>
                <Select
                  onValueChange={handleAccountChange}
                  value={selectedAccountId}
                  disabled={isLoadingAccounts || accounts.length === 0}
                >
                  <SelectTrigger className="w-full border-1 border-muted-foreground/20 bg-muted/40">
                    <SelectValue
                      placeholder={
                        isLoadingAccounts
                          ? 'Loading accounts...'
                          : accounts.length > 0
                            ? 'Select an account'
                            : 'No accounts available'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="border-0 max-h-72">
                    {accounts.map((account) => (
                      <SelectItem
                        key={account.id || account._id}
                        value={account.id || account._id}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{account.name || 'N/A'}</span>
                          
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">
                  Customer User <span className="text-destructive">*</span>
                </Label>
                <Select
                  onValueChange={setSelectedCustomerUserId}
                  value={selectedCustomerUserId}
                  disabled={!selectedAccountId || isLoadingCustomers || customerUsers.length === 0}
                >
                  <SelectTrigger className="w-full border-1 border-muted-foreground/20 bg-muted/40">
                    <SelectValue
                      placeholder={
                        !selectedAccountId
                          ? 'Select an account first'
                          : isLoadingCustomers
                            ? 'Loading customers...'
                            : customerUsers.length > 0
                              ? 'Select a customer'
                              : 'No customers available'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="border-0 max-h-72">
                    {customerUsers.map((customer) => {
                      const customerId =
                        customer?.customer_user_id ||
                        customer?.customerUserId ||
                        customer?._id ||
                        customer?.id ||
                        null;
                      const customerName =
                        customer?.name ||
                        [customer?.first_name, customer?.last_name].filter(Boolean).join(' ') ||
                        customer?.email ||
                        'Customer';

                      return (
                        <SelectItem key={customerId} value={customerId}>
                          <div className="flex flex-col">
                            <span className="font-medium flex items-center gap-2">
                              <Users className="w-3.5 h-3.5 text-muted-foreground" />
                              {customerName}
                            </span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeMoveDialog}
                className="px-4 py-2 border-0 bg-muted/50"
                disabled={isMoving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMoveDevice}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white"
                disabled={isMoving || !selectedAccountId || !selectedCustomerUserId}
              >
                {isMoving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Moving...
                  </>
                ) : (
                  'Move Device'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AllCustomerDevices;